import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  TablePagination,
  Chip,
  Backdrop,
} from '@mui/material';
import { Add, Edit, Delete, Visibility } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import {
  getPublicEntities,
  deletePublicEntity,
} from '../../services/publicEntityService';
import { CityResponse } from '../../types/city';
import { getCities } from '../../services/cityService';
import { PaginatedPublicEntityResponse } from '../../types/publicEntity';
import useAuth from '../../hooks/useAuth';
import swal from 'sweetalert';
import Select from 'react-select';
import { useTheme } from '@mui/material/styles';


interface PublicEntity {
  id: string;
  name: string;
  city: {
    id: string;
    name: string;
  };
}

const PublicEntitiesList: React.FC = () => {
  const [publicEntities, setPublicEntities] = useState<PublicEntity[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const { user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  // Estados de Paginação
  const [page, setPage] = useState<number>(0); // Zero-based index
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalItems, setTotalItems] = useState<number>(0);

  // Estados de Filtragem
  const [cities, setCities] = useState<CityResponse[]>([]);
  const [selectedCity, setSelectedCity] = useState<string | undefined>(undefined);

  /**
   * Função para converter uma string em uma cor HSL única.
   * Esta função gera uma cor baseada no hash do nome da cidade.
   * @param str - Nome da cidade.
   * @returns Uma string representando uma cor HSL.
   */
  const stringToHSL = (str: string): string => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const hue = hash % 360;
    return `hsl(${hue}, 70%, 50%)`;
  };

  /**
   * Função para buscar entidades públicas com base na cidade e na página atual.
   */
  const fetchPublicEntities = async (cityId?: string, currentPage: number = 0, currentSize: number = 10) => {
    setLoading(true);
    setError(false);
    try {
      const data: PaginatedPublicEntityResponse = await getPublicEntities(
        cityId,
        currentPage + 1, // API espera página 1-based
        currentSize
      );
      setPublicEntities(data.items);
      setTotalItems(data.total);
      setPage(data.page - 1); // Ajusta para zero-based index
    } catch (err) {
      console.error('Erro ao buscar entidades públicas:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Função para buscar a lista de cidades para o filtro.
   */
  const fetchCities = async () => {
    try {
      const data = await getCities();
      setCities(data);
    } catch (error) {
      console.error('Erro ao buscar cidades:', error);
      swal('Erro', 'Não foi possível buscar as cidades.', 'error');
    }
  };

  useEffect(() => {
    fetchCities();
  }, []);

  useEffect(() => {
    fetchPublicEntities(selectedCity, page, rowsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCity, page, rowsPerPage]);

  /**
   * Função para lidar com a deleção de uma entidade pública.
   */
  const handleDelete = async (entityId: string) => {
    const confirm = await swal({
      title: 'Tem certeza?',
      text: 'Você tem certeza que deseja deletar esta entidade pública?',
      icon: 'warning',
      buttons: ['Cancelar', 'Deletar'],
      dangerMode: true,
    });

    if (confirm) {
      try {
        await deletePublicEntity(entityId);
        swal('Deletado!', 'Entidade pública deletada com sucesso.', 'success');
        fetchPublicEntities(selectedCity, page, rowsPerPage);
      } catch (error) {
        console.error('Erro ao deletar entidade pública:', error);
        swal('Erro', 'Não foi possível deletar a entidade pública.', 'error');
      }
    }
  };

  /**
   * Função para lidar com a mudança de página na paginação.
   */
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  /**
   * Função para lidar com a mudança no número de linhas por página.
   */
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Resetar para a primeira página
  };

  /**
   * Função para lidar com a seleção de uma cidade no filtro.
   */
  const handleCityChange = (selectedOption: any) => {
    setSelectedCity(selectedOption ? selectedOption.value : undefined);
    setPage(0); // Resetar para a primeira página ao filtrar
  };

  return (
    <Box>
      {/* Tratamento de Erro */}
      {error && (
        <Backdrop
          open={true}
          sx={{
            zIndex: 1300,
            backdropFilter: 'blur(8px)',
            WebkitBackdropFilter: 'blur(8px)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            flexDirection: 'column',
            textAlign: 'center',
            p: 3,
          }}
        >
          <Typography variant="h6" sx={{ mb: 1 }}>
            Ops... Ocorreu um erro!
          </Typography>
          <Typography variant="subtitle1" sx={{ maxWidth: '80%', mx: 'auto' }}>
            Verifique sua conexão ou tente novamente mais tarde.
          </Typography>
          <Button variant="contained" color="primary" onClick={() => fetchPublicEntities(selectedCity, page, rowsPerPage)} sx={{ mt: 2 }}>
            Tentar Novamente
          </Button>
        </Backdrop>
      )}

      {/* Conteúdo Principal */}
      {!error && (
        <>
          {/* Cabeçalho com Título e Botão de Adição */}
          <Box
            sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
          >
            <Typography variant="h5">Entidades Públicas</Typography>
            {user?.is_system_admin && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<Add />}
                onClick={() => navigate('/entidades-publicas/novo')}
              >
                Adicionar Entidade
              </Button>
            )}
          </Box>

          {/* Filtro por Cidade */}
          <Box sx={{ mb: 2, maxWidth: 300 }}>
            <Select
              options={cities.map((city) => ({ value: city.id, label: city.name }))}
              isClearable
              placeholder="Filtrar por Cidade..."
              onChange={handleCityChange}
            />
          </Box>

          {/* Estado de Carregamento */}
          {loading ? (
            <Box display="flex" justifyContent="center" alignItems="center" sx={{ mt: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ mb: 4 }}>
              {/* Tabela de Entidades Públicas */}
              <TableContainer component={Paper}>
                <Table aria-label="public entities table">
                  <TableHead>
                    <TableRow>
                      <TableCell>Nome</TableCell>
                      <TableCell>Cidade</TableCell>
                      <TableCell align="right">Ações</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {publicEntities.map((entity) => (
                      <TableRow key={entity.id} hover>
                        <TableCell>{entity.name}</TableCell>
                        <TableCell>
                          <Chip
                            label={entity.city.name}
                            sx={{
                              backgroundColor: stringToHSL(entity.city.name),
                              color: theme.palette.getContrastText(stringToHSL(entity.city.name)),
                            }}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Visualizar">
                            <IconButton onClick={() => navigate(`/entidades-publicas/${entity.id}`)}>
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          {user?.is_system_admin && (
                            <>
                              <Tooltip title="Editar">
                                <IconButton onClick={() => navigate(`/entidades-publicas/${entity.id}/editar`)}>
                                  <Edit />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Deletar">
                                <IconButton onClick={() => handleDelete(entity.id)}>
                                  <Delete />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {publicEntities.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} align="center">
                          Nenhuma entidade pública encontrada.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Controles de Paginação */}
              <TablePagination
                component="div"
                count={totalItems}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[5, 10, 25, 50]}
                labelRowsPerPage="Registros por página"
              />
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default PublicEntitiesList;